import { ErrorBanner, type Liveness, LivenessPill, type SendGate } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import { ConnectDeskButton } from '../features/auth/connect-desk-button'
import { deskConnectNeeded, useMe } from '../features/auth/session'
import { ApiUnreachableError, isRequestAbort } from '../lib/api/client'
import { type ApiHealth, clockTime, useApiHealth } from '../lib/api/health'
import { type LivenessView, livenessQuery } from '../lib/api/queries'
import { formatWhen } from '../lib/display-time'

/**
 * What the shell knows about its own health (scope §5.0 States, P11, P12), in one place: the liveness
 * pill, the banner under the top bar, and the send gate every ComposerShell reads.
 *
 * Sends fail closed: unknown liveness, an unreachable API, a red mirror, or `sends_disabled` from the
 * API all disable every send box. Amber only colours the pill.
 */
export interface ShellStatus {
  api: ApiHealth
  liveness: LivenessView | null
  livenessUnknown: boolean
  /** Red mirror, or the API says sends are disabled. */
  mirrorStale: boolean
  pillState: Liveness
  gate: SendGate
}

const PILL: Record<LivenessView['mirrorState'], Liveness> = { ok: 'fresh', amber: 'lagging', red: 'stale', unknown: 'stale' }

export const MIRROR_STALE = 'Mirror stale; sends disabled'

export function isMirrorStale(l: LivenessView | null): boolean {
  return Boolean(l && (l.mirrorState === 'red' || l.mirrorState === 'unknown' || l.sendsDisabled))
}

/** The banner's second line. A quiet Desk is never the reason (the API stays ok while Riley is idle too). */
export function mirrorStaleDetail(l: LivenessView | null): string {
  const reason = l?.sendsDisabledReason
  if (reason && reason !== MIRROR_STALE) return reason
  return 'Desk activity is not reaching Patchbay, or Riley could not confirm Desk is just quiet.'
}

export function computeSendGate(input: {
  api: ApiHealth
  liveness: LivenessView | null
  livenessUnknown: boolean
  deskWriteAllowed: boolean
  deskConnectNeeded: boolean
  connectAction?: React.ReactNode
}): SendGate {
  const { api, liveness } = input
  if (!api.reachable) {
    return { disabled: true, reason: `Patchbay API unreachable${api.since ? ` since ${clockTime(api.since)}` : ''}. Read-only.` }
  }
  if (input.livenessUnknown || !liveness) return { disabled: true, reason: 'Desk mirror state unknown; sends disabled' }
  if (isMirrorStale(liveness)) {
    if (liveness.rileyBanner) return { disabled: true, reason: liveness.rileyBanner }
    const why = liveness.sendsDisabledReason
    return { disabled: true, reason: why && why !== MIRROR_STALE ? `${MIRROR_STALE} (${why})` : MIRROR_STALE }
  }
  if (!input.deskWriteAllowed) return { disabled: true, reason: 'Your role has no send box' }
  if (input.deskConnectNeeded) return { disabled: true, reason: 'Connect Desk to send', action: input.connectAction }
  return { disabled: false }
}

export function useShellStatus(): ShellStatus {
  const api = useApiHealth()
  const me = useMe()
  const q = useQuery(livenessQuery())
  const liveness = q.data ?? null
  // A cancelled superseded refetch and a transport error inside the API grace window keep the last completed
  // liveness answer. A malformed/error response is still unknown immediately; an outage becomes unknown at 10 s.
  const transportFailure = isRequestAbort(q.error) || q.error instanceof ApiUnreachableError
  const livenessUnknown = !api.reachable || (q.isError && !transportFailure) || (!q.data && !q.isPending && !transportFailure)
  const needsDesk = deskConnectNeeded(me)
  const gate = React.useMemo(
    () =>
      computeSendGate({
        api,
        liveness,
        livenessUnknown: livenessUnknown || (q.isPending && !q.data),
        deskWriteAllowed: me?.can_write_desk ?? false,
        deskConnectNeeded: needsDesk,
        connectAction: needsDesk ? <ConnectDeskButton size="sm" /> : undefined,
      }),
    [api, liveness, livenessUnknown, q.isPending, q.data, me?.can_write_desk, needsDesk],
  )
  return {
    api,
    liveness,
    livenessUnknown,
    mirrorStale: !livenessUnknown && isMirrorStale(liveness),
    pillState:
      api.state === 'reconnecting' || ((q.isPending || transportFailure) && !q.data)
        ? 'lagging'
        : livenessUnknown || !liveness
          ? 'stale'
          : PILL[liveness.mirrorState],
    gate,
  }
}

/** Desk quiet for this long while the API says the mirror is fine: say "Quiet since 14:05", not an alarming age. */
export const QUIET_AFTER_MINUTES = 15

export function quietSinceOf(l: LivenessView | null, unknown: boolean): string | undefined {
  if (unknown || !l || l.mirrorState !== 'ok' || l.sendsDisabled) return undefined
  if (l.deskEventAgeMinutes === null || l.deskEventAgeMinutes < QUIET_AFTER_MINUTES || !l.lastDeskEventAt) return undefined
  const at = new Date(l.lastDeskEventAt)
  if (Number.isNaN(at.getTime())) return undefined
  return formatWhen(at.toISOString(), { style: 'time' })
}

export function ShellLivenessPill({ status }: { status: ShellStatus }) {
  return (
    <LivenessPill
      ageMinutes={status.liveness?.deskEventAgeMinutes ?? null}
      state={status.pillState}
      unknown={status.livenessUnknown}
      quietSince={quietSinceOf(status.liveness, status.livenessUnknown)}
      label={status.api.state === 'reconnecting' ? 'Reconnecting…' : status.api.state === 'disconnected' ? 'API disconnected' : undefined}
    />
  )
}

/** The one banner slot: API down, then reconnecting, then Riley stopped, then mirror stale, then Connect Desk. */
export function ShellBanner({ status }: { status: ShellStatus }) {
  const me = useMe()
  if (!status.api.reachable) {
    return (
      <div className="px-4 py-2" data-banner="api-down">
        <ErrorBanner
          title={`Patchbay API unreachable${status.api.since ? ` since ${clockTime(status.api.since)}` : ''}. Read-only.`}
          detail="Showing what this browser last loaded. Sends are disabled until the API answers again."
        />
      </div>
    )
  }
  if (status.api.state === 'reconnecting') {
    return (
      <div className="px-4 py-2" data-banner="api-reconnecting">
        <ErrorBanner
          tone="warning"
          title="Reconnecting…"
          detail="An API request did not answer. Keeping the current screen while Patchbay reconnects."
        />
      </div>
    )
  }
  if (status.liveness?.rileyBanner) {
    return (
      <div className="px-4 py-2" data-banner="riley-down">
        <ErrorBanner
          tone={status.mirrorStale ? 'danger' : 'warning'}
          title={status.liveness.rileyBanner}
          detail={
            status.mirrorStale
              ? 'Sends are disabled until Riley is processing again.'
              : 'Sends stay available. This notice is because Riley is not finishing its work.'
          }
        />
      </div>
    )
  }
  if (status.mirrorStale) {
    return (
      <div className="px-4 py-2" data-banner="mirror-stale">
        <ErrorBanner title={MIRROR_STALE} detail={mirrorStaleDetail(status.liveness)} />
      </div>
    )
  }
  if (status.livenessUnknown) {
    return (
      <div className="px-4 py-2" data-banner="liveness-unknown">
        <ErrorBanner tone="warning" title="Desk mirror state unknown; sends disabled" detail="Liveness could not be read." />
      </div>
    )
  }
  if (deskConnectNeeded(me)) {
    return (
      <div className="flex items-center gap-3 border-b border-border bg-warning-soft px-4 py-2 text-sm" data-banner="connect-desk">
        <span className="flex-1">
          {me?.desk_token === 'refresh_failing'
            ? 'Your Desk connection stopped working. Replies and time wait until you reconnect.'
            : 'Connect Desk to send. Replies and time are written to Desk with your own token.'}
        </span>
        <ConnectDeskButton size="sm" />
      </div>
    )
  }
  return null
}
